import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EmailEditor from "@/components/EmailEditor";
import { BarChart3, Mail, Users, Send, Plus, Trash2, Eye, MousePointer, UserMinus, Inbox, ChevronRight, Loader2, MailOpen } from "lucide-react";
import { toast } from "sonner";

export default function MarketingPage() {
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", preview_text: "", html_content: "" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [a, c, s, i] = await Promise.all([
        api.get("/marketing/analytics"), api.get("/marketing/campaigns"),
        api.get("/marketing/subscribers"), api.get("/marketing/inbox"),
      ]);
      setAnalytics(a.data); setCampaigns(c.data); setSubscribers(s.data); setInbox(i.data);
    } catch (err) { console.error(err); }
  };

  const openNewCampaign = () => {
    setForm({ name: "", subject: "", preview_text: "", html_content: "" });
    setEditingCampaign(null);
    setEditorOpen(true);
  };

  const openEditCampaign = (c) => {
    setForm({ name: c.name, subject: c.subject, preview_text: c.preview_text || "", html_content: c.html_content || "" });
    setEditingCampaign(c);
    setEditorOpen(true);
  };

  const saveCampaign = async () => {
    if (!form.name || !form.subject) { toast.error("Name and subject are required"); return; }
    setSaving(true);
    try {
      if (editingCampaign) {
        await api.put(`/marketing/campaigns/${editingCampaign.id}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/marketing/campaigns", form);
        toast.success("Campaign created");
      }
      setEditorOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const sendCampaign = async (id) => {
    if (!confirm("Send this campaign to all active subscribers?")) return;
    setSending(id);
    try {
      const res = await api.post(`/marketing/campaigns/${id}/send`);
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(null); }
  };

  const deleteCampaign = async (id) => {
    if (!confirm("Delete this campaign?")) return;
    try { await api.delete(`/marketing/campaigns/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const viewCampaignDetail = async (id) => {
    try {
      const { data } = await api.get(`/marketing/campaigns/${id}`);
      setDetailCampaign(data);
    } catch { toast.error("Failed to load"); }
  };

  const markRead = async (id) => {
    try { await api.put(`/marketing/inbox/${id}/read`); fetchAll(); } catch {}
  };

  const statCards = analytics ? [
    { label: "Active Subscribers", value: analytics.active_subscribers, color: "text-green-400", icon: Users },
    { label: "Unsubscribed", value: analytics.total_unsubscribed, color: "text-red-400", icon: UserMinus },
    { label: "Campaigns Sent", value: analytics.sent_campaigns, color: "text-blue-400", icon: Send },
    { label: "Emails Sent", value: analytics.total_sent, color: "text-cyan-400", icon: Mail },
    { label: "Open Rate", value: `${analytics.open_rate}%`, color: "text-[#F7941D]", icon: MailOpen },
    { label: "Click Rate", value: `${analytics.click_rate}%`, color: "text-purple-400", icon: MousePointer },
    { label: "Churn Rate", value: `${analytics.churn_rate}%`, color: "text-rose-400", icon: UserMinus },
    { label: "Unread Replies", value: analytics.unread_replies, color: "text-yellow-400", icon: Inbox },
  ] : [];

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div className="animate-fade-in-up">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight">Email Marketing</h1>
        <p className="text-white/35 text-sm mt-1">Create campaigns, track opens, manage subscribers</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white/[0.04] rounded-xl border border-white/[0.06] flex-wrap">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="mkt-tab-overview"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="mkt-tab-campaigns"><Mail className="w-3.5 h-3.5 mr-1.5" />Campaigns</TabsTrigger>
          <TabsTrigger value="subscribers" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="mkt-tab-subscribers"><Users className="w-3.5 h-3.5 mr-1.5" />Subscribers</TabsTrigger>
          <TabsTrigger value="inbox" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40 relative" data-testid="mkt-tab-inbox">
            <Inbox className="w-3.5 h-3.5 mr-1.5" />Inbox
            {analytics?.unread_replies > 0 && <span className="ml-1.5 bg-[#F7941D] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{analytics.unread_replies}</span>}
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
            {statCards.map(({ label, value, color, icon: Icon }) => (
              <div key={label} className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`w-4 h-4 ${color}`} strokeWidth={1.5} />
                </div>
                <p className={`font-mono text-2xl font-semibold ${color}`}>{value}</p>
                <p className="text-xs text-white/30 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CAMPAIGNS */}
        <TabsContent value="campaigns">
          <div className="flex justify-between items-center mb-4">
            <p className="text-white/40 text-sm">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}</p>
            <button onClick={openNewCampaign} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-4 py-2 text-sm transition-all duration-300 flex items-center gap-1.5" data-testid="new-campaign-btn">
              <Plus className="w-4 h-4" />New Campaign
            </button>
          </div>

          {campaigns.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <Mail className="w-10 h-10 text-white/15 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No campaigns yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-3 stagger">
              {campaigns.map((c) => (
                <div key={c.id} className="glass glass-hover rounded-2xl p-5 animate-fade-in-up" data-testid={`campaign-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-white/80 truncate">{c.name}</h3>
                        <Badge className={
                          c.status === 'sent' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                          c.status === 'sending' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                          'bg-white/[0.06] text-white/40 border-white/[0.08]'
                        } variant="outline">{c.status}</Badge>
                      </div>
                      <p className="text-xs text-white/30 truncate">{c.subject}</p>
                      {c.status === 'sent' && (
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/30">
                          <span><Eye className="w-3 h-3 inline mr-1" />{c.total_opened}/{c.total_sent} opened</span>
                          <span><MousePointer className="w-3 h-3 inline mr-1" />{c.total_clicked} clicked</span>
                          <span><UserMinus className="w-3 h-3 inline mr-1" />{c.total_unsubscribed} unsub</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.status === 'sent' && (
                        <button onClick={() => viewCampaignDetail(c.id)} className="glass rounded-lg px-3 py-1.5 text-xs text-white/50 hover:text-white/80 transition-colors" data-testid={`view-campaign-${c.id}`}>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {c.status === 'draft' && (
                        <>
                          <button onClick={() => openEditCampaign(c)} className="glass rounded-lg px-3 py-1.5 text-xs text-white/50 hover:text-white/80 transition-colors">Edit</button>
                          <button onClick={() => sendCampaign(c.id)} disabled={sending === c.id} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-lg px-3 py-1.5 text-xs transition-all duration-300 flex items-center gap-1" data-testid={`send-campaign-${c.id}`}>
                            {sending === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Send
                          </button>
                        </>
                      )}
                      <button onClick={() => deleteCampaign(c.id)} className="text-red-400/50 hover:text-red-400 transition-colors p-1" data-testid={`delete-campaign-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SUBSCRIBERS */}
        <TabsContent value="subscribers">
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-5 pb-3 flex justify-between items-center">
              <p className="text-sm text-white/50">{subscribers.filter(s => s.subscribed).length} active / {subscribers.length} total</p>
            </div>
            <div className="px-5 pb-5 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Name</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Email</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Referred By</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Status</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} className="border-b border-white/[0.03] last:border-0">
                      <td className="text-sm text-white/70 py-3 pr-4">{s.first_name} {s.last_name}</td>
                      <td className="text-xs text-white/40 font-mono py-3 pr-4">{s.email}</td>
                      <td className="text-xs text-[#F7941D] py-3 pr-4">{s.ambassador_name}</td>
                      <td className="py-3 pr-4">
                        <Badge className={s.subscribed ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'} variant="outline">
                          {s.subscribed ? 'Active' : 'Unsubscribed'}
                        </Badge>
                      </td>
                      <td className="text-xs text-white/25 text-right font-mono py-3">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {subscribers.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-white/25 py-12">No subscribers yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* INBOX */}
        <TabsContent value="inbox">
          {inbox.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <Inbox className="w-10 h-10 text-white/15 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No replies yet.</p>
              <p className="text-white/20 text-xs mt-1">Replies from campaigns will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3 stagger">
              {inbox.map((r) => (
                <div key={r.id} className={`glass rounded-2xl p-5 cursor-pointer transition-all duration-300 ${r.is_read ? 'opacity-60' : 'border-l-2 border-l-[#F7941D]'}`} onClick={() => markRead(r.id)}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-white/80">{r.from_email}</p>
                        {!r.is_read && <span className="w-2 h-2 rounded-full bg-[#F7941D]" />}
                      </div>
                      <p className="text-xs text-white/50 mb-1">{r.subject}</p>
                      <p className="text-xs text-white/30 line-clamp-2">{r.body_text}</p>
                    </div>
                    <span className="text-xs text-white/20 shrink-0 font-mono">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* CAMPAIGN EDITOR DIALOG */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="glass-strong rounded-2xl border-white/[0.08] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white/90">{editingCampaign ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-sm">Campaign Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., June Launch Announcement" className="glass-input rounded-xl h-11 text-sm" data-testid="campaign-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-sm">Subject Line</Label>
                <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="e.g., Eamico Studio is launching!" className="glass-input rounded-xl h-11 text-sm" data-testid="campaign-subject-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-sm">Preview Text <span className="text-white/25">(shown in inbox preview)</span></Label>
              <Input value={form.preview_text} onChange={e => setForm({ ...form, preview_text: e.target.value })} placeholder="Brief preview text..." className="glass-input rounded-xl h-11 text-sm" data-testid="campaign-preview-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-sm">Email Content</Label>
              <EmailEditor content={form.html_content} onChange={(html) => setForm({ ...form, html_content: html })} />
              <p className="text-xs text-white/20">Use <code className="text-[#F7941D]/60">{"{{first_name}}"}</code> for personalization. Links are auto-tracked.</p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={saveCampaign} disabled={saving} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-6 py-2.5 text-sm transition-all duration-300 disabled:opacity-50" data-testid="save-campaign-btn">
                {saving ? "Saving..." : editingCampaign ? "Update Campaign" : "Create Campaign"}
              </button>
              <button onClick={() => setEditorOpen(false)} className="glass rounded-full px-6 py-2.5 text-sm text-white/50 hover:text-white/80 transition-colors">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CAMPAIGN DETAIL DIALOG */}
      <Dialog open={!!detailCampaign} onOpenChange={() => setDetailCampaign(null)}>
        <DialogContent className="glass-strong rounded-2xl border-white/[0.08] max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailCampaign && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white/90">{detailCampaign.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "Sent", value: detailCampaign.total_sent, color: "text-blue-400" },
                    { label: "Opened", value: detailCampaign.total_opened, color: "text-[#F7941D]" },
                    { label: "Clicked", value: detailCampaign.total_clicked, color: "text-purple-400" },
                    { label: "Unsub", value: detailCampaign.total_unsubscribed, color: "text-red-400" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="glass rounded-xl p-3 text-center">
                      <p className={`font-mono text-xl font-semibold ${color}`}>{value}</p>
                      <p className="text-xs text-white/30">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="glass rounded-2xl overflow-hidden">
                  <div className="p-4 pb-2"><p className="text-sm text-white/50">Recipients</p></div>
                  <div className="px-4 pb-4 max-h-60 overflow-y-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/[0.06]">
                          <th className="text-left text-white/35 text-xs py-2">Email</th>
                          <th className="text-left text-white/35 text-xs py-2">Status</th>
                          <th className="text-right text-white/35 text-xs py-2">Opened</th>
                          <th className="text-right text-white/35 text-xs py-2">Clicked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailCampaign.sends || []).map((s, i) => (
                          <tr key={i} className="border-b border-white/[0.03] last:border-0">
                            <td className="text-xs text-white/50 font-mono py-2">{s.recipient_email}</td>
                            <td className="py-2">
                              <Badge className={
                                s.status === 'clicked' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                                s.status === 'opened' ? 'bg-[#F7941D]/10 text-[#F7941D] border-[#F7941D]/20' :
                                'bg-white/[0.04] text-white/30 border-white/[0.06]'
                              } variant="outline">{s.status}</Badge>
                            </td>
                            <td className="text-xs text-white/25 text-right font-mono py-2">{s.opened_at ? new Date(s.opened_at).toLocaleString() : '-'}</td>
                            <td className="text-xs text-white/25 text-right font-mono py-2">{s.clicked_at ? new Date(s.clicked_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
